import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, catchError, combineLatest, distinctUntilChanged, finalize, forkJoin, map, of, switchMap, throwError } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TagModule } from 'primeng/tag';
import { BackButton } from '@components/ui/back-button/back-button';
import { Loading } from '@components/ui/loading/loading';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { DatePickerComponent } from '@components/date-picker/date-picker.component';
import { DataTableComponent, TableColumn, TableConfig } from '@components/data-table/data-table.component';
import { DateFormatLangPipe } from '@app/utils/pipes/date-format-lang-pipe';
import { AssignmentProcessService as AssignmentProcessApiService } from '@6grain/masofa-monolith-api-client/common/api/assignmentProcess.service';
import { ProcessSubscriptionService as ProcessSubscriptionApiService } from '@6grain/masofa-monolith-api-client/common/api/processSubscription.service';
import { MonitoredOperationPageResponse } from '@6grain/masofa-monolith-api-client/common/model/monitoredOperationPageResponse';
import { ProcessSubscriptionResponse } from '@6grain/masofa-monolith-api-client/common/model/processSubscriptionResponse';
import { SubscriptionFormDialogComponent } from '@app/pages/admin/components/subscription-form-dialog/subscription-form-dialog';
import {
  INTERRUPTION_RESUME_SAME,
  PAUSE_REASON_CONSECUTIVE_FAILURES,
  toSubscriptionProcessOptions,
} from '@app/pages/admin/process-subscription-form.util';
import { PrettyJsonPipe } from '@app/utils/pipes/pretty-json.pipe';
import { operationDetailsUrl } from '@app/pages/admin/operation-details.util';
import { formatQuartzCronLabel } from '@app/pages/admin/orchestration-quartz-cron.util';
import { formatTimeZoneLabel } from '@app/pages/admin/orchestration-time-zones.util';
import {
  namedKeyLabel,
  orchestrationProcessUrl,
  orchestrationSubscriptionUrl,
} from '@app/pages/admin/orchestration-identity.util';
import {
  ASSIGNMENT_STATE_FILTERS,
  AssignmentStateFilter,
  TagSeverity,
  countAssignmentsByStateFilter,
  filterAssignmentsByCreatedRange,
  filterAssignmentsByState,
} from '@app/pages/admin/orchestration-process-page/orchestration-process-page.util';
import { toRecordPageHeading } from '@app/pages/admin/orchestration-record-heading.util';
import {
  AssignmentResultFilter,
  assignmentResultFilterAfterCreate,
  assignmentResultFilterQuery,
} from '@app/pages/admin/assignment-result-count.util';
import { AssignmentResultFilterComponent } from '@app/pages/admin/components/assignment-result-filter/assignment-result-filter';
import { AssignmentResultCellComponent } from '@app/pages/admin/components/assignment-result-cell/assignment-result-cell';
import { AssignmentRssCellComponent } from '@app/pages/admin/components/assignment-rss-cell/assignment-rss-cell';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import { AssignmentHostLabelComponent } from '@app/pages/admin/components/assignment-host-label/assignment-host-label';
import { OrchestrationTableHeadingComponent } from '@app/pages/admin/components/orchestration-table-heading/orchestration-table-heading';
import { filterByCatalogQuery } from '@app/pages/admin/orchestration-catalog-search.util';
import {
  SubscriptionAssignmentRow,
  parseSubscriptionAssignmentFilterQuery,
  serializeSubscriptionAssignmentFilterQuery,
  toSubscriptionAssignmentRow,
} from './orchestration-subscription-page.util';

interface ProcessDefinition {
  key: string;
  version?: number;
  displayName?: string;
}

interface SubscriptionView {
  id: string;
  name: string;
  processKey: string;
  processLabel: string;
  processUrl: string | null;
  cronLabel: string;
  timeZoneLabel: string;
  isActive: boolean;
  statusLabel: 'Active' | 'Inactive';
  statusSeverity: TagSeverity;
  nextFireAt?: string | null;
  lastTriggeredAt?: string | null;
  lastAssignmentId: string | null;
  lastAssignmentUrl: string | null;
  safeInputJson: string | null;
  interruptionLabel: string;
  resumeAttemptLimit: number | null;
  pauseAfterConsecutiveFailures: number;
  pauseReasonLabel: string | null;
}

interface AssignmentStateChip {
  id: AssignmentStateFilter;
  label: string;
  count: number;
  selected: boolean;
}

@Component({
  selector: 'app-orchestration-subscription-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    TagModule,
    BackButton,
    Loading,
    PrimaryButton,
    SecondaryButton,
    DatePickerComponent,
    DataTableComponent,
    DateFormatLangPipe,
    PrettyJsonPipe,
    SubscriptionFormDialogComponent,
    AssignmentResultFilterComponent,
    AssignmentResultCellComponent,
    AssignmentRssCellComponent,
    CopyableIdentifierComponent,
    AssignmentHostLabelComponent,
    OrchestrationTableHeadingComponent,
  ],
  templateUrl: './orchestration-subscription-page.html',
  styleUrl: './orchestration-subscription-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
})
export class OrchestrationSubscriptionPageComponent implements OnInit, OnDestroy {
  private readonly subscriptionsApi = inject(ProcessSubscriptionApiService);
  private readonly processesApi = inject(AssignmentProcessApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly translate = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();
  private readonly reload$ = new Subject<string>();
  private lastLoadedAssignments: { subscriptionId: string; result: AssignmentResultFilter } | null = null;

  private readonly formDialog = viewChild.required(SubscriptionFormDialogComponent);
  private readonly assignmentNameTemplate = viewChild<TemplateRef<unknown>>('assignmentNameTemplate');
  private readonly assignmentHostTemplate = viewChild<TemplateRef<unknown>>('assignmentHostTemplate');
  private readonly assignmentStateTemplate = viewChild<TemplateRef<unknown>>('assignmentStateTemplate');
  private readonly assignmentCountTemplate = viewChild<TemplateRef<unknown>>('assignmentCountTemplate');
  private readonly assignmentRssTemplate = viewChild<TemplateRef<unknown>>('assignmentRssTemplate');
  private readonly processTemplate = viewChild<TemplateRef<unknown>>('processTemplate');

  readonly subscriptionId = signal('');
  readonly source = signal<ProcessSubscriptionResponse | null>(null);
  readonly processes = signal<ProcessDefinition[]>([]);
  readonly subscription = signal<SubscriptionView | null>(null);
  readonly assignmentRows = signal<SubscriptionAssignmentRow[]>([]);
  readonly loading = signal(false);
  readonly running = signal(false);
  readonly notFound = signal(false);
  readonly errorMessage = signal('');
  readonly stateFilter = signal<AssignmentStateFilter>('all');
  readonly resultFilter = signal<AssignmentResultFilter>('positive');
  readonly createdRange = signal<Date[] | null>(null);
  readonly assignmentSearch = signal('');

  readonly pageHeading = computed(() => toRecordPageHeading({
    meaning: this.subscription()?.name === '—' ? null : this.subscription()?.name,
    key: this.subscription()?.processKey,
    id: this.subscription()?.id,
    fallbackTitle: 'Subscription details',
  }));

  readonly datedAssignments = computed(() =>
    filterAssignmentsByCreatedRange(this.assignmentRows(), this.createdRange()),
  );

  readonly visibleAssignments = computed(() =>
    filterByCatalogQuery(
      filterAssignmentsByState(this.datedAssignments(), this.stateFilter()),
      this.assignmentSearch(),
      row => [
        row.name,
        row.processKey,
        row.processLabel,
        row.state,
        row.stateLabel,
        row.progressLabel,
        row.hostLabel,
        row.workerServiceName,
        row.workerHostName,
        row.workerVersion,
        row.error,
        row.resultCount == null ? '' : String(row.resultCount),
      ],
    ),
  );

  readonly stateChips = computed<AssignmentStateChip[]>(() => {
    const rows = this.datedAssignments();
    const selected = this.stateFilter();
    return ASSIGNMENT_STATE_FILTERS.map(id => ({
      id,
      label: id === 'all' ? 'All' : id,
      count: countAssignmentsByStateFilter(rows, id),
      selected: selected === id,
    }));
  });

  readonly assignmentColumns = computed<TableColumn[]>(() => [
    {
      field: 'name',
      header: 'Assignment name',
      type: 'custom',
      customTemplate: this.assignmentNameTemplate(),
    },
    {
      field: 'processLabel',
      header: 'Process',
      type: 'custom',
      customTemplate: this.processTemplate(),
    },
    {
      field: 'state',
      header: 'Status',
      type: 'custom',
      customTemplate: this.assignmentStateTemplate(),
    },
    {
      field: 'hostLabel',
      header: 'Executed on',
      type: 'custom',
      customTemplate: this.assignmentHostTemplate(),
    },
    { field: 'createdAt', header: 'Created', type: 'date', dateTime: true },
    { field: 'progressLabel', header: 'Progress', type: 'text' },
    { field: 'durationLabel', header: 'Duration', type: 'text' },
    {
      field: 'resultCount',
      header: 'Result',
      type: 'custom',
      customTemplate: this.assignmentCountTemplate(),
    },
    {
      field: 'rss',
      header: 'RSS',
      type: 'custom',
      customTemplate: this.assignmentRssTemplate(),
    },
  ]);

  readonly assignmentTableConfig: TableConfig = {
    paginator: true,
    rows: 25,
    keepOrder: true,
    emptyMessage: 'No assignments for this subscription',
  };

  ngOnInit(): void {
    this.reload$.pipe(
      switchMap(subscriptionId => {
        this.loading.set(true);
        this.errorMessage.set('');
        this.notFound.set(false);
        return this.subscriptionsApi.processSubscriptionsSubscriptionIdGet(subscriptionId).pipe(
          catchError(error => this.recoverSubscriptionFromList(subscriptionId, error)),
          switchMap(item => forkJoin({
            item: of(item),
            operations: this.subscriptionsApi.processSubscriptionsSubscriptionIdAssignmentsGet(
              subscriptionId,
              1,
              200,
              assignmentResultFilterQuery(this.resultFilter()),
            ).pipe(catchError(error => this.emptyAssignmentsIfMissing(error))),
            processes: this.processesApi.assignmentsProcessesGet(),
          })),
          finalize(() => this.loading.set(false)),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: ({ item, operations, processes }) => {
        const processList = processes as ProcessDefinition[];
        this.source.set(item);
        this.processes.set(processList);
        this.subscription.set(this.toView(item, processList));
        this.assignmentRows.set(
          (operations.items ?? []).map(row => toSubscriptionAssignmentRow(row, processList)),
        );
      },
      error: (error: unknown) => {
        this.source.set(null);
        if (error instanceof HttpErrorResponse && error.status === 404) {
          this.notFound.set(true);
          this.subscription.set(null);
          this.assignmentRows.set([]);
          return;
        }
        this.errorMessage.set('Failed to load subscription');
        this.subscription.set(null);
      },
    });

    combineLatest([
      this.route.paramMap.pipe(
        map(params => params.get('subscriptionId') ?? ''),
        distinctUntilChanged(),
      ),
      this.route.queryParamMap,
    ]).pipe(
      takeUntil(this.destroy$),
    ).subscribe(([subscriptionId, query]) => {
      const parsed = parseSubscriptionAssignmentFilterQuery(query);
      if (subscriptionId !== this.subscriptionId()) {
        this.subscriptionId.set(subscriptionId);
        this.source.set(null);
        this.subscription.set(null);
        this.assignmentRows.set([]);
        this.lastLoadedAssignments = null;
      }
      this.resultFilter.set(parsed.result);
      this.stateFilter.set(parsed.state);
      this.createdRange.set(parsed.createdRange);
      if (!subscriptionId) {
        return;
      }
      const alreadyLoaded = this.lastLoadedAssignments?.subscriptionId === subscriptionId
        && this.lastLoadedAssignments.result === parsed.result;
      if (alreadyLoaded) {
        return;
      }
      this.lastLoadedAssignments = { subscriptionId, result: parsed.result };
      this.reload$.next(subscriptionId);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.reload$.complete();
  }

  reload(): void {
    const subscriptionId = this.subscriptionId();
    if (subscriptionId) {
      this.reload$.next(subscriptionId);
    }
  }

  selectStateFilter(filter: AssignmentStateFilter): void {
    this.stateFilter.set(filter);
    this.syncAssignmentFilterQuery();
  }

  onResultFilterChange(filter: AssignmentResultFilter): void {
    this.resultFilter.set(filter);
    this.syncAssignmentFilterQuery();
  }

  onCreatedRangeChange(value: Date[] | Date | null): void {
    if (Array.isArray(value)) {
      this.createdRange.set(value.filter((entry): entry is Date => entry instanceof Date));
    } else {
      this.createdRange.set(null);
    }
    this.syncAssignmentFilterQuery();
  }

  edit(): void {
    const item = this.source();
    if (!item) {
      return;
    }
    this.formDialog().openEdit(toSubscriptionProcessOptions(this.processes()), item);
  }

  copy(): void {
    const item = this.source();
    if (!item) {
      return;
    }
    this.formDialog().openCopy(toSubscriptionProcessOptions(this.processes()), item);
  }

  onSubscriptionSaved(saved: ProcessSubscriptionResponse): void {
    const nextUrl = orchestrationSubscriptionUrl(saved.id);
    if (nextUrl && saved.id && saved.id !== this.subscriptionId()) {
      void this.router.navigateByUrl(nextUrl);
      return;
    }
    this.reload();
  }

  runNow(): void {
    const subscriptionId = this.subscriptionId();
    if (!subscriptionId || this.running()) {
      return;
    }
    this.running.set(true);
    this.errorMessage.set('');
    this.subscriptionsApi.processSubscriptionsSubscriptionIdRunPost(subscriptionId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.running.set(false)),
      )
      .subscribe({
        next: () => {
          const alreadyAll = this.resultFilter() === 'all';
          this.resultFilter.set(assignmentResultFilterAfterCreate());
          this.syncAssignmentFilterQuery();
          if (alreadyAll) {
            this.reload();
          }
        },
        error: () => {
          this.errorMessage.set('Failed to run process subscription');
        },
      });
  }

  openAssignment(row: SubscriptionAssignmentRow): void {
    if (!row.id) {
      return;
    }
    void this.router.navigateByUrl(operationDetailsUrl('Assignment', row.id));
  }

  private syncAssignmentFilterQuery(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: serializeSubscriptionAssignmentFilterQuery({
        result: this.resultFilter(),
        state: this.stateFilter(),
        createdRange: this.createdRange(),
      }),
      replaceUrl: true,
    });
  }

  private emptyAssignmentsIfMissing(error: unknown) {
    if (error instanceof HttpErrorResponse && error.status === 404) {
      const empty: MonitoredOperationPageResponse = {
        items: [],
        totalCount: 0,
        page: 1,
        pageSize: 200,
      };
      return of(empty);
    }
    return throwError(() => error);
  }

  private recoverSubscriptionFromList(subscriptionId: string, error: unknown) {
    if (!(error instanceof HttpErrorResponse) || error.status !== 404) {
      return throwError(() => error);
    }
    return this.subscriptionsApi.processSubscriptionsGet().pipe(
      map(items => {
        const found = (items ?? []).find(item => item.id === subscriptionId);
        if (!found) {
          throw error;
        }
        return found;
      }),
    );
  }

  private toView(
    item: ProcessSubscriptionResponse,
    processes: ProcessDefinition[],
  ): SubscriptionView {
    const processKey = item.processDefinitionKey ?? '';
    const lastAssignmentId = item.lastAssignmentId ?? null;
    const resumeSame = item.interruptionPolicy === INTERRUPTION_RESUME_SAME;
    return {
      id: item.id ?? '',
      name: item.name?.trim() || '—',
      processKey,
      processLabel: namedKeyLabel(processes, processKey) || processKey || '—',
      processUrl: orchestrationProcessUrl(processKey),
      cronLabel: formatQuartzCronLabel(item.cronExpression ?? '', (key, params) =>
        this.translate.instant(key, params),
      ),
      timeZoneLabel: item.timeZoneId ? formatTimeZoneLabel(item.timeZoneId) : '—',
      isActive: item.isActive === true,
      statusLabel: item.isActive ? 'Active' : 'Inactive',
      statusSeverity: item.isActive ? 'success' : 'warn',
      nextFireAt: item.nextFireAt,
      lastTriggeredAt: item.lastTriggeredAt,
      lastAssignmentId,
      lastAssignmentUrl: lastAssignmentId
        ? operationDetailsUrl('Assignment', lastAssignmentId)
        : null,
      safeInputJson: item.safeInputJson ?? null,
      interruptionLabel: resumeSame ? 'Continue the same assignment' : 'Wait for the next run',
      resumeAttemptLimit: resumeSame ? (item.resumeAttemptLimit ?? 1) : null,
      pauseAfterConsecutiveFailures: item.pauseAfterConsecutiveFailures ?? 0,
      pauseReasonLabel: item.pauseReason === PAUSE_REASON_CONSECUTIVE_FAILURES
        ? 'Paused after consecutive failed assignments'
        : null,
    };
  }
}
